/**
 * The engine-pairing seam — web mirror of `Sources/Store/BridgeTokensStore.swift`.
 *
 * Mirrors the MemoriesStore pattern: one interface the Setup screen talks to, with
 * a Mock implementation (offline sample data) and a live implementation
 * (`BrainBridgeTokensStore`) that hits the Brain Worker's bridge-token API with the
 * caller's `brain_*` token (NOT the short-lived Supabase JWT). Backend selection
 * follows the SAME switch the app already uses: mock → MockBridgeTokensStore,
 * supabase → BrainBridgeTokensStore.
 *
 * A `brdg_*` bridge token lets a local bridge daemon drain ONLY the issuing
 * tenant's task queue — it is not a `brain_` account token and grants nothing else.
 * See claude-brain-mcp docs/PAIRING.md and this repo's docs/OPERATOR_GUIDE.md.
 *
 * Endpoints (all authenticated with `Authorization: Bearer brain_*`):
 *   • POST /bridge-tokens        {label?,executor?} → 201 { token (brdg_… once), id, label, created_at, note }
 *   • GET  /bridge-tokens        → { tokens: [ { id, label, executor, created_at, last_used_at, revoked_at } ] }
 *   • POST /bridge-tokens/revoke { token_id }       → { revoked: true }
 *
 * SECURITY: the plaintext `brdg_` token is returned by mint ONCE and never
 * persisted or re-shown; list/revoke only ever expose metadata (never hashes).
 */

/** One bridge-token row from `GET /bridge-tokens` — metadata only. */
export interface BridgeTokenInfo {
  id: string;
  label: string | null;
  executor: string | null;
  created_at: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
}

/** The result of `POST /bridge-tokens` — the ONLY time the plaintext exists. */
export interface MintedBridgeToken {
  token: string;
  id: string;
  label: string | null;
  created_at: string | null;
  note: string | null;
}

export interface BridgeTokensStore {
  /** POST /bridge-tokens — mint a new `brdg_` token (plaintext returned ONCE). */
  mint(opts?: { label?: string; executor?: string }): Promise<MintedBridgeToken>;

  /** GET /bridge-tokens — the caller's tokens (metadata only). */
  list(): Promise<BridgeTokenInfo[]>;

  /** POST /bridge-tokens/revoke — revoke by id (immediate on the next poll). */
  revoke(tokenId: string): Promise<void>;
}

/** A revoked token has a non-empty `revoked_at`. */
export function isRevoked(t: BridgeTokenInfo): boolean {
  return !!t.revoked_at;
}

export class BridgeTokensStoreError extends Error {
  constructor(
    message: string,
    public status?: number,
  ) {
    super(message);
    this.name = 'BridgeTokensStoreError';
  }
}

/** Map the API's documented statuses to a friendly, user-facing line. */
export function friendlyBridgeError(status: number, body: string): string {
  switch (status) {
    case 401:
      return 'Not authorized — your Brain session may have expired.';
    case 404:
      return 'That token no longer exists (or is already revoked).';
    default:
      return `Brain returned HTTP ${status}: ${body}`;
  }
}
